/**
 * ScorePanel Component
 * Shows the AI score and, beside it, the user's personal score ("With your edge") once their edge over the
 * AI is proven, or the edge status until then (backlog 240).
 */

import UpgradeModal from '@components/ui/UpgradeModal';
import { useEdgeStatus } from '@hooks/useEdgeStatus';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { edgeBadge, edgeLegChanges, PERSONAL_SCORE_LABEL } from '@parlay-picker/shared/edgeStatus';
import { getNormalizedFromParlayScore } from '@parlay-picker/shared/scoreDisplay';
import { TrendingDown } from 'lucide-react';
import React, { useState } from 'react';
import { EdgeStatusPill } from './score-panel/EdgeStatusPill';
import { PropHighlightBadges } from './score-panel/PropHighlightBadges';
import { ScorePanelTooltip } from './score-panel/ScorePanelTooltip';
import { getBadgeStyle } from './score-panel/scorePanelUtils';
import type { ScorePanelProps } from './score-panel/types';

export function ScorePanel({
  dynamicScore,
  predictionScore,
  className = '',
  scoresUnavailable = false,
  onRefresh,
  legs,
}: ScorePanelProps): React.ReactElement | null {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [activeTab, setActiveTab] = useState<'bets' | 'ai'>('bets');
  const [tooltipPosition, setTooltipPosition] = useState<'above' | 'below'>('below');
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { isLocked } = useFeatureAccess();
  const scoringLocked = isLocked('scoring_breakdown');
  const deepAnalysisLocked = isLocked('scoring_deep_analysis');
  const [upgradeFeature, setUpgradeFeature] = useState<
    'scoring_breakdown' | 'scoring_deep_analysis' | 'history_personal'
  >('scoring_breakdown');
  const edge = edgeBadge(useEdgeStatus());

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshAttempted, setRefreshAttempted] = useState(false);

  const handleRefresh = async () => {
    if (!onRefresh || isRefreshing) return;
    setIsRefreshing(true);
    setRefreshAttempted(false);
    try {
      await onRefresh();
      setRefreshAttempted(true);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleOpenUpgrade = (
    feature: 'scoring_breakdown' | 'scoring_deep_analysis' | 'history_personal',
  ) => {
    setShowTooltip(false);
    setUpgradeFeature(feature);
    setShowUpgrade(true);
  };

  const updateTooltipPosition = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    setTooltipPosition(spaceBelow < 450 ? 'above' : 'below');
  };

  if (scoresUnavailable && !dynamicScore && !predictionScore) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-theme-surface/50 border border-theme-border/50">
          <span className="text-theme-text-secondary text-xs">
            {refreshAttempted ? 'No score data yet' : 'Scores unavailable'}
          </span>
          {onRefresh && (
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`text-xs font-medium ml-1 ${isRefreshing ? 'text-theme-text-tertiary cursor-wait animate-spin' : 'text-theme-primary hover:text-theme-primary-hover'}`}
              title="Try again"
            >
              ↻
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!dynamicScore && !predictionScore) return null;

  const activeScore =
    activeTab === 'bets' ? (dynamicScore ?? predictionScore) : (predictionScore ?? dynamicScore);
  if (!activeScore) return null;

  // Which tab is actually shown, auto-falling back when one score is null so an
  // AI-only pick (community score, no personal score) marks the AI badge active
  // and labels its context correctly (mirrors apps/mobile ScorePanel).
  const effectiveTab: 'bets' | 'ai' =
    activeTab === 'bets' ? (dynamicScore ? 'bets' : 'ai') : predictionScore ? 'ai' : 'bets';

  const hasHighlights = activeScore.playerHighlights.length > 0;

  return (
    <div
      ref={containerRef}
      className={`relative ${showTooltip ? 'z-40' : ''} ${className}`}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Side-by-side badges */}
      <div className="flex items-center gap-2">
        {/* Edge status, until there is a personal score to show */}
        {!dynamicScore && edge && edge.kind !== 'proven' && (
          <EdgeStatusPill badge={edge} onUpgrade={() => handleOpenUpgrade('history_personal')} />
        )}

        {/* Personal score badge */}
        {dynamicScore &&
          (() => {
            const { displayScore: dynDisplay, displayColor: dynColor } =
              getNormalizedFromParlayScore(dynamicScore);
            const dynStyle = getBadgeStyle(dynColor, dynamicScore.playerWarnings.length > 0);
            return (
              <button
                type="button"
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                  effectiveTab === 'bets' ? dynStyle.bg : 'bg-theme-surface'
                } text-white border ${
                  effectiveTab === 'bets' ? dynStyle.border : 'border-theme-border'
                } shadow-sm cursor-help transition hover:scale-105`}
                onMouseEnter={() => {
                  setActiveTab('bets');
                  updateTooltipPosition();
                  setShowTooltip(true);
                }}
                onClick={() => {
                  setActiveTab('bets');
                  updateTooltipPosition();
                  setShowTooltip(!showTooltip);
                }}
                title="Your personal score"
                data-testid="edge-score-you"
              >
                {edge?.kind === 'proven' && edge.below && (
                  <TrendingDown className="w-3 h-3" aria-label="Trails the AI" />
                )}
                <span className="text-[10px] opacity-80">{PERSONAL_SCORE_LABEL}</span>
                <span className="font-bold">{dynDisplay}</span>
              </button>
            );
          })()}

        {/* AI Picks badge */}
        {predictionScore &&
          (() => {
            const { displayScore: predDisplay, displayColor: predColor } =
              getNormalizedFromParlayScore(predictionScore);
            const predStyle = getBadgeStyle(predColor, predictionScore.playerWarnings.length > 0);
            return (
              <button
                type="button"
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                  effectiveTab === 'ai' ? predStyle.bg : 'bg-theme-surface'
                } text-white border ${
                  effectiveTab === 'ai' ? predStyle.border : 'border-theme-border'
                } shadow-sm cursor-help transition hover:scale-105`}
                onMouseEnter={() => {
                  setActiveTab('ai');
                  updateTooltipPosition();
                  setShowTooltip(true);
                }}
                onClick={() => {
                  setActiveTab('ai');
                  updateTooltipPosition();
                  setShowTooltip(!showTooltip);
                }}
                title="Score based on all AI predictions"
              >
                <span>📊</span>
                <span className="text-[10px] opacity-80">AI</span>
                <span className="font-bold">{predDisplay}</span>
              </button>
            );
          })()}

        {/* Hot players indicator */}
        {!scoringLocked && hasHighlights && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gradient-to-r from-theme-success to-emerald-400 text-white">
            {activeScore.playerHighlights.length} ⭐
          </span>
        )}

        {/* Prop type highlights */}
        {!scoringLocked && (
          <PropHighlightBadges
            propHighlights={activeScore.propTypeHighlights}
            legs={legs}
            context="AI predictions"
          />
        )}
      </div>

      {showTooltip && (
        <ScorePanelTooltip
          activeScore={activeScore}
          activeTab={effectiveTab}
          edgeHint={edge?.kind === 'proven' ? edge.hint : undefined}
          edgeLegs={edgeLegChanges(predictionScore, dynamicScore, legs)}
          tooltipPosition={tooltipPosition}
          scoringLocked={scoringLocked}
          deepAnalysisLocked={deepAnalysisLocked}
          onOpenUpgrade={handleOpenUpgrade}
        />
      )}

      {showUpgrade && (
        <UpgradeModal
          mode="feature"
          feature={upgradeFeature}
          onDismiss={() => setShowUpgrade(false)}
        />
      )}
    </div>
  );
}
